import { createClient } from '@supabase/supabase-js'

// Configuração do Supabase (Updated)
const supabaseUrl = 'https://uhrjhjctyxxzdpvgjrct.supabase.co'
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVocmpoamN0eXh4emRwdmdqcmN0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUxMDEzNzMsImV4cCI6MjA3MDY3NzM3M30.l2gfnkwJ9w1FIoGTEPzJ6Tf4lHQQoBLuEWe74fwCY5Y'

// Criar cliente Supabase
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Tipos para o banco de dados
export type UserRole = 'patient' | 'doctor' | 'admin'
export type AccessStatus = 'pending' | 'approved' | 'denied' | 'revoked'
export type RecordType = 'consultation' | 'diagnosis' | 'prescription' | 'lab_result' | 'imaging' | 'surgery' | 'vaccination'

export interface UserProfile {
  id: string
  role: UserRole
  full_name: string
  email: string
  phone?: string
  date_of_birth?: string
  gender?: string
  address?: any
  medical_license?: string
  specialization?: string
  hospital_affiliation?: string
  emergency_contact?: any
  blockchain_address?: string
  public_key_pqc?: string
  created_at: string
  updated_at: string
}

export interface MedicalRecord {
  id: string
  patient_id: string
  doctor_id: string
  record_type: string
  title: string
  description?: string
  diagnosis?: string
  treatment?: string
  medications?: any
  lab_results?: any
  vital_signs?: any
  attachments?: any
  encrypted_data?: string
  encryption_key_id?: string
  blockchain_hash?: string
  blockchain_tx_id?: string
  ipfs_hash?: string
  is_encrypted: boolean
  created_at: string
  updated_at: string
  // Relacionamentos
  patient?: UserProfile
  doctor?: UserProfile
}

export interface AccessPermission {
  id: string
  patient_id: string
  requester_id: string
  record_id?: string
  permission_type: string
  status: AccessStatus
  granted_at?: string
  expires_at?: string
  revoked_at?: string
  blockchain_tx_id?: string
  smart_contract_address?: string
  created_at: string
  updated_at: string
  // Relacionamentos
  patient?: UserProfile
  requester?: UserProfile
  record?: MedicalRecord
}

export interface BlockchainAudit {
  id: string
  entity_type: string
  entity_id: string
  action_type: string
  user_id: string
  blockchain_network: string
  transaction_hash: string
  block_number?: number
  gas_used?: number
  smart_contract_address?: string
  event_data?: any
  created_at: string
  // Relacionamentos
  user?: UserProfile
}

export interface PQCKey {
  id: string
  user_id: string
  key_purpose: string
  public_key: string
  private_key_encrypted: string
  algorithm: string
  key_size?: number
  is_active: boolean
  created_at: string
  expires_at?: string
  metadata?: any
}

export interface SharingSession {
  id: string
  patient_id: string
  requester_id: string
  session_token: string
  encrypted_key: string
  permissions: any
  is_active: boolean
  expires_at: string
  created_at: string
  // Relacionamentos
  patient?: UserProfile
  requester?: UserProfile
}

// Funções de autenticação
export const auth = {
  // Registrar novo usuário
  async signUp(email: string, password: string, userData: { full_name: string; role: UserRole }) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
    return { data, error }
  },

  // Login
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Logout
  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Obter usuário atual
  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  // Obter sessão atual
  async getSession() {
    return await supabase.auth.getSession()
  },

  // Obter perfil do usuário
  async getUserProfile(userId: string): Promise<{ data: UserProfile | null; error: any }> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single()
    return { data, error }
  },

  // Atualizar perfil do usuário
  async updateUserProfile(userId: string, updates: Partial<UserProfile>) {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    return { data, error }
  },

  // Escutar mudanças de autenticação
  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Funções para registros médicos
export const medicalRecords = {
  // Buscar registros do paciente
  async getPatientRecords(patientId: string) {
    const { data, error } = await supabase
      .from('medical_records')
      .select(`
        *,
        doctor:user_profiles!medical_records_doctor_id_fkey(*)
      `)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Buscar registros criados pelo médico
  async getDoctorRecords(doctorId: string) {
    const { data, error } = await supabase
      .from('medical_records')
      .select('*')
      .eq('doctor_id', doctorId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Buscar registros que o médico pode acessar através de permissões
  async getDoctorAccessibleRecords(doctorId: string) {
    // Buscar registros que o médico tem acesso através de permissões
    const { data: permissions } = await supabase
      .from('access_permissions')
      .select('record_id')
      .eq('doctor_id', doctorId)
      .eq('status', 'approved')
    
    if (!permissions || permissions.length === 0) {
      return { data: [], error: null }
    }
    
    const recordIds = permissions.map(p => p.record_id).filter(Boolean)
    
    const { data, error } = await supabase
      .from('medical_records')
      .select('*')
      .in('id', recordIds)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Criar novo registro médico
  async createRecord(record: Omit<MedicalRecord, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('medical_records')
      .insert(record)
      .select()
      .single()
    return { data, error }
  },

  // Atualizar registro médico
  async updateRecord(recordId: string, updates: Partial<MedicalRecord>) {
    const { data, error } = await supabase
      .from('medical_records')
      .update(updates)
      .eq('id', recordId)
      .select()
      .single()
    return { data, error }
  }
}

// Funções para controle de acesso
export const accessControl = {
  // Buscar permissões do paciente
  async getPatientPermissions(patientId: string) {
    const { data, error } = await supabase
      .from('access_permissions')
      .select(`
        *,
        requester:user_profiles!access_permissions_doctor_id_fkey(*),
        record:medical_records(*)
      `)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Buscar permissões do médico
  async getDoctorPermissions(doctorId: string) {
    const { data, error } = await supabase
      .from('access_permissions')
      .select(`
        *,
        patient:user_profiles!access_permissions_patient_id_fkey(*),
        record:medical_records(*)
      `)
      .eq('doctor_id', doctorId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Solicitar acesso
  async requestAccess(permission: Omit<AccessPermission, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('access_permissions')
      .insert(permission)
      .select()
      .single()
    return { data, error }
  },

  // Aprovar/negar acesso
  async updatePermissionStatus(permissionId: string, status: AccessStatus, grantedAt?: string) {
    const updates: any = { status }
    if (status === 'approved' && grantedAt) {
      updates.granted_at = grantedAt
    }
    if (status === 'revoked') {
      updates.revoked_at = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('access_permissions')
      .update(updates)
      .eq('id', permissionId)
      .select()
      .single()
    return { data, error }
  }
}

// Funções para auditoria blockchain
export const blockchainAudit = {
  // Buscar logs de auditoria do usuário
  async getUserAuditLogs(userId: string) {
    const { data, error } = await supabase
      .from('blockchain_audit')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Criar log de auditoria
  async createAuditLog(audit: Omit<BlockchainAudit, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('blockchain_audit')
      .insert(audit)
      .select()
      .single()
    return { data, error }
  }
}

// Funções para chaves PQC
export const pqcKeys = {
  // Buscar chaves do usuário
  async getUserKeys(userId: string) {
    const { data, error } = await supabase
      .from('pqc_keys')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Criar nova chave
  async createKey(key: Omit<PQCKey, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('pqc_keys')
      .insert(key)
      .select()
      .single()
    return { data, error }
  },

  // Desativar chave
  async deactivateKey(keyId: string) {
    const { data, error } = await supabase
      .from('pqc_keys')
      .update({ is_active: false })
      .eq('id', keyId)
      .select()
      .single()
    return { data, error }
  }
}